import React from "react";
import { CurrencyDollar, Lightning, Plugs, Certificate } from "@phosphor-icons/react";
import { useI18n } from "../../i18n/I18nContext";

const ICONS = {
  "proof-throughput": Lightning,
  "proof-patents": Certificate,
  "proof-mcp": Plugs,
};

const PARTNERS_RU = [
  "NVIDIA DCGM",
  "Kubernetes",
  "Kueue",
  "MIG",
  "NVLink",
  "InfiniBand",
  "Model Context Protocol",
  "Сколково",
  "NVIDIA Inception",
  "Prometheus",
  "Grafana",
  "Triton Inference",
];

const PARTNERS_EN = [
  "NVIDIA DCGM",
  "Kubernetes",
  "Kueue",
  "MIG",
  "NVLink",
  "InfiniBand",
  "Model Context Protocol",
  "NVIDIA Inception",
  "Prometheus",
  "Grafana",
  "Triton Inference Server",
];

export const SocialProof = () => {
  const { isEn, t } = useI18n();

  const partnerList = isEn ? PARTNERS_EN : PARTNERS_RU;
  const marqueeItems = partnerList.concat(partnerList);

  return (
    <section data-testid="social-proof-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-12">
          <div className="mono text-xs uppercase tracking-[0.22em] text-brand-cyan mb-3">
            {t.socialProof.sectionNum}
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-3xl">
            {t.socialProof.headlinePart1}{" "}
            <span className="text-brand-cyan">{t.socialProof.headlinePart2}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Hero case card */}
          <div
            data-testid="case-13m"
            className="lg:col-span-7 relative rounded-2xl glass-strong p-10 sm:p-12 overflow-hidden"
          >
            <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-brand/20 blur-[120px]" />
            <div className="absolute -bottom-24 -right-24 w-[320px] h-[320px] rounded-full bg-brand-cyan/15 blur-[120px]" />

            <div className="relative">
              <div className="flex items-center gap-2 mb-6">
                <CurrencyDollar size={18} weight="fill" className="text-brand-cyan" />
                <div className="mono text-xs uppercase tracking-[0.22em] text-brand-cyan">
                  {t.socialProof.caseStudyTag}
                </div>
              </div>

              <div className="font-heading font-black tracking-tighter leading-none">
                <span className="text-5xl sm:text-7xl lg:text-8xl mono bg-gradient-to-r from-brand to-brand-cyan bg-clip-text text-transparent">
                  {t.socialProof.caseValue}
                </span>
                <span className="text-2xl sm:text-3xl text-ink-text ml-3">
                  {t.socialProof.casePerYear}
                </span>
              </div>

              <div className="mt-5 text-muted text-base sm:text-lg max-w-xl leading-relaxed">
                {t.socialProof.caseDesc}
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                {t.socialProof.stats.map(([v, l]) => (
                  <div key={l} className="rounded-xl bg-ink-elevated border border-ink-text/5 p-3">
                    <div className="mono text-xl font-bold text-brand-cyan">{v}</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted mt-1">
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column proofs */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {t.socialProof.proofs.map((p) => {
              const Icon = ICONS[p.id] || Lightning;
              return (
                <div
                  key={p.id}
                  data-testid={p.id}
                  className="glass rounded-2xl p-7 relative overflow-hidden flex-1"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-cyan/10 border border-brand-cyan/30 flex items-center justify-center">
                      <Icon size={20} weight="duotone" className="text-brand-cyan" />
                    </div>
                    <div className="font-heading font-semibold">{p.title}</div>
                  </div>
                  <div className="font-heading font-black mono text-4xl text-brand-cyan text-glow-cyan">
                    {p.value}
                  </div>
                  <p className="mt-3 text-sm text-muted leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Logos / partners marquee */}
        <div className="mt-16 overflow-hidden">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted mb-5 text-center">
            {t.socialProof.partnersHeader}
          </div>
          <div className="relative">
            <div className="marquee whitespace-nowrap flex gap-12 mono text-sm text-muted/80">
              {marqueeItems.map((partnerName, i) => (
                <span key={i} className="flex items-center gap-3">
                  <span className="w-1 h-1 rounded-full bg-brand-cyan" />
                  {partnerName}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
